import { Fragment } from 'react';
import {
	Grid,
	TextField,
	Typography,
	Box,
	Button,
	Autocomplete,
	IconButton,
	Switch
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material/styles';

interface Field {
	id: number;
	field1: string;
	field2: string;
}

const GroupingAlertTab = ({groupingAlertsConfig = {}, setGroupingAlertsConfig}: any) => {
	const theme = useTheme();
	const colors = theme.palette;

	const timeframeOptions = ['Minutes', 'Hours', 'Days', 'Weeks', 'Months']
	
	const handleChange = ({ name, value }: any) => {
		setGroupingAlertsConfig({...groupingAlertsConfig, [name]: value})
	};

	const handleFieldChange = (index: number, value: string) => {
		const identifiers = groupingAlertsConfig.identifiers.map((field: string, idx: number) => {
			if(idx === index){
				field = value
			}
			return field
		})
		setGroupingAlertsConfig({...groupingAlertsConfig, identifiers})
	};

	const addField = () => {
		const identifiers = groupingAlertsConfig.identifiers ? [...groupingAlertsConfig.identifiers, ''] : ['']
		setGroupingAlertsConfig({...groupingAlertsConfig, identifiers})
	};

	const removeField = (index: number) => {
		setGroupingAlertsConfig({
			...groupingAlertsConfig,
			identifiers: groupingAlertsConfig.identifiers.filter(
				(_: string, idx: number) => idx !== index
			)
		})
	};

	const inputSx = {
		width: '100%',

		'& .MuiOutlinedInput-root': {
			backgroundColor: `${colors.inputNew.inputBackground} !important`,
			color: `${colors.inputNew.inputText} !important`,

			'& fieldset': {
				borderColor: `${colors.inputNew.inputBorder} !important`
			},

			'&:hover fieldset': {
				borderColor: `${colors.inputNew.inputBorderHover} !important`
			},

			'&.Mui-focused fieldset': {
				borderColor: `${colors.inputNew.inputBorderFocus} !important`
			},

			'& input': {
				color: `${colors.inputNew.inputText} !important`,
				WebkitTextFillColor: `${colors.inputNew.inputText} !important`,

				'&::placeholder': {
					color: `${colors.inputNew.inputPlaceholder} !important`,
					WebkitTextFillColor: `${colors.inputNew.inputPlaceholder} !important`,
					opacity: 1
				}
			}
		},

		'& .MuiInputLabel-root': {
			color: `${colors.inputNew.inputPlaceholder} !important`
		},

		'& .MuiInputLabel-root.Mui-focused': {
			color: `${colors.inputNew.inputPlaceholder} !important`
		}
	};

	const autocompleteSx = {
		width: '100%',

		'& .MuiOutlinedInput-root': {
			backgroundColor: `${colors.DropdownV2.background} !important`,
			color: `${colors.DropdownV2.menuItemText} !important`,

			'& fieldset': {
				borderColor: `${colors.DropdownV2.borderColor} !important`
			},

			'&:hover fieldset': {
				borderColor: `${colors.DropdownV2.borderColorHover} !important`
			},

			'&.Mui-focused fieldset': {
				borderColor: `${colors.DropdownV2.borderColorActive} !important`,
				borderWidth: '1px'
			}
		},

		'& .MuiInputLabel-root': {
			color: `${colors.inputNew.inputPlaceholder} !important`
		},

		'& .MuiInputLabel-root.Mui-focused': {
			color: `${colors.inputNew.inputPlaceholder} !important`
		},

		'& .MuiInputBase-input': {
			color: `${colors.DropdownV2.menuItemText} !important`,
			WebkitTextFillColor: `${colors.DropdownV2.menuItemText} !important`
		},

		'& .MuiAutocomplete-popupIndicator': {
			color: `${colors.DropdownV2.dropdownIconDefault} !important`
		},

		'& .MuiAutocomplete-popupIndicatorOpen': {
			color: `${colors.DropdownV2.dropdownIconActive} !important`
		},

		'& .MuiAutocomplete-popupIndicator svg': {
			color: 'inherit !important',
			width: '22px',
			height: '22px'
		},

		'& .MuiAutocomplete-clearIndicator': {
			color: `${colors.DropdownV2.dropdownIconDefault} !important`
		},

		'& .MuiOutlinedInput-root.Mui-focused .MuiAutocomplete-popupIndicator': {
			color: `${colors.DropdownV2.dropdownIconActive} !important`
		}
	};

	const dropdownPaperSx = {
		mt: 1,
		overflow: 'hidden',
		backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
		color: `${colors.DropdownV2.menuItemText} !important`,
		boxShadow: '0px 8px 24px rgba(16, 24, 40, 0.12)',

		'& .MuiAutocomplete-listbox': {
			padding: 0,
			backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
			color: `${colors.DropdownV2.menuItemText} !important`
		},

		'& .MuiAutocomplete-option': {
			color: `${colors.DropdownV2.menuItemText} !important`,
			backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
			padding: '10px 16px',
			minHeight: '50px',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			gap: '12px',
			opacity: 1
		},

		'& .MuiAutocomplete-option:hover': {
			backgroundColor: `${colors.DropdownV2.menuItemHover} !important`,
			color: `${colors.DropdownV2.menuItemHoverText} !important`
		},

		'& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option.Mui-focusVisible': {
			backgroundColor: `${colors.DropdownV2.menuItemHover} !important`,
			color: `${colors.DropdownV2.menuItemText} !important`
		},

		'& .MuiAutocomplete-option[aria-selected="true"]': {
			backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
			color: `${colors.DropdownV2.menuItemSelectedText} !important`,
			opacity: 1
		},

		'& .MuiAutocomplete-option[aria-selected="true"]:hover, & .MuiAutocomplete-option[aria-selected="true"].Mui-focused, & .MuiAutocomplete-option[aria-selected="true"].Mui-focusVisible': {
			backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
			color: `${colors.DropdownV2.menuItemSelectedText} !important`,
			opacity: 1
		},

		'& .dropdown-option-text': {
			color: `${colors.DropdownV2.menuItemText} !important`
		},

		'& .MuiAutocomplete-option[aria-selected="true"] .dropdown-option-text': {
			color: `${colors.DropdownV2.menuItemSelectedText} !important`
		},

		'& .dropdown-option-check': {
			color: `${colors.DropdownV2.tickColor} !important`
		},

		'& .dropdown-option-check path': {
			fill: `${colors.DropdownV2.tickColor} !important`
		},

		'& .MuiAutocomplete-noOptions': {
			color: `${colors.DropdownV2.mutedText} !important`
		}
	};

	const switchSx = {
		width: 34,
		height: 20,
		padding: 0,

		'& .MuiSwitch-switchBase': {
			padding: '3px',
			color: colors.toggle.thumb,

			'&.Mui-checked': {
				transform: 'translateX(14px)',
				color: colors.toggle.thumb,

				'& + .MuiSwitch-track': {
					backgroundColor: colors.toggle.trackOn,
					opacity: 1
				}
			},

			'&.Mui-disabled + .MuiSwitch-track': {
				backgroundColor: colors.toggle.disabledTrack,
				opacity: 1
			}
		},

		'& .MuiSwitch-thumb': {
			width: 14,
			height: 14,
			backgroundColor: colors.toggle.thumb,
			boxShadow: colors.toggle.thumbShadow
		},

		'& .MuiSwitch-track': {
			borderRadius: '10px',
			backgroundColor: colors.toggle.trackOff,
			opacity: 1
		}
	};

	return (
		<Box sx={{ mt: 3, padding: '20px', borderRadius: '8px' }}>
			<Typography
				variant="body2"
				sx={{
					opacity: 0.7,
					mb: 2,
					color: `${colors.textLight.main} !important`
				}}
			>
				ⓘ This section is to configure grouped alerts.
			</Typography>
			
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-start',
							alignItems: 'center',
							mt: 1,
							mb: 1
						}}
					>
						<Switch
							sx={{
								mr: 1,
								...switchSx
							}}
							onChange={(e) => handleChange({ name: 'isEnabled', value: e.target.checked })}
							checked={groupingAlertsConfig.isEnabled}
							size="small"
						/>
						<Typography
							variant="tabPageHeader"
							sx={{
								color: `${colors.modal.sectionTitle} !important`
							}}
						>
							Grouping of Alerts
						</Typography>
					</Box>
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<TextField
						label="Group Name*"
						variant="outlined"
						size="small"
						value={groupingAlertsConfig.name}
						onChange={(e) => handleChange({ name: 'name', value: e.target.value })}
						sx={inputSx}
					/>
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<TextField
						label="Duration*"
						variant="outlined"
						size="small"
						type={'number'}
						value={groupingAlertsConfig.timeframeValue}
						onChange={(e) => handleChange({ name: 'timeframeValue', value: e.target.value })}
						sx={inputSx}
					/>
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<Autocomplete
						id="size-small-outlined"
						size="small"
						options={timeframeOptions}
						value={groupingAlertsConfig.timeframeUnit}
						onChange={(e, option) => handleChange({ name: 'timeframeUnit', value: option })}
						popupIcon={<KeyboardArrowDownIcon />}
						sx={autocompleteSx}
						componentsProps={{
							paper: {
								sx: dropdownPaperSx
							},
							popupIndicator: {
								sx: {
									color: `${colors.DropdownV2.dropdownIconActive} !important`,

									'&:hover': {
										backgroundColor: 'transparent'
									}
								}
							},
							clearIndicator: {
								sx: {
									color: `${colors.DropdownV2.menuItemText} !important`,

									'&:hover': {
										backgroundColor: 'transparent'
									}
								}
							}
						}}
						renderOption={(props, option, { selected }) => (
							<Box
								component="li"
								{...props}
							>
								<Typography
									className="dropdown-option-text"
									sx={{
										flex: 1,
										color: selected
											? `${colors.DropdownV2.menuItemSelectedText} !important`
											: `${colors.DropdownV2.menuItemText} !important`
									}}
								>
									{option}
								</Typography>

								{selected ? (
									<CheckIcon
										className="dropdown-option-check"
										sx={{
											fontSize: '22px',
											color: `${colors.DropdownV2.tickColor} !important`,
											flexShrink: 0
										}}
									/>
								) : null}
							</Box>
						)}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Select unit*"
								variant="outlined"
							/>
						)}
					/>
				</Grid>
			</Grid>

			<Typography
				variant="tabPageHeader"
				sx={{
					display: 'block',
					mt: 2,
					mb: 2,
					color: `${colors.modal.sectionTitle} !important`
				}}
			>
				Dynamic Fields*
			</Typography>

			<Grid container spacing={2} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
				{groupingAlertsConfig.identifiers?.map((field: string, index: number) => (
					<Fragment key={`${field}-${index}`}>
						<Grid item xs={10} sm={6} md={4}>
							<TextField
								label={`Select Field ${index + 1}`}
								variant="outlined"
								size="small"
								value={field}
								onChange={(e) => handleFieldChange(index, e.target.value)}
								sx={inputSx}
							/>
						</Grid>

						<Grid item xs={2} sm={1} md={1}>
							<IconButton
								onClick={() => removeField(index)}
								aria-label="remove field"
								sx={{
									color: `${colors.inputNew.removeIcon} !important`,
									border: `1px solid ${colors.inputNew.removeBorder}`,
									borderRadius: '4px',
									p: '4px',

									'&:hover': {
										backgroundColor: `${colors.inputNew.removeHoverBackground} !important`
									}
								}}
							>
								<CloseIcon
									sx={{
										color: `${colors.inputNew.removeIcon} !important`
									}}
								/>
							</IconButton>
						</Grid>
					</Fragment>
				))}

				{/* Add Field Button in the Same Row */}
				<Grid item xs={12}>
					<Button
						variant="text"
						startIcon={
							<AddIcon
								sx={{
									color: `${colors.buttons.primary.textColor} !important`,
									fill: `${colors.buttons.primary.textColor} !important`
								}}
							/>
						}
						onClick={addField}
						sx={{
							mt: 2,
							color: `${colors.buttons.primary.textColor} !important`,
							fontWeight: 600,
							backgroundColor: 'transparent !important',
							'& .MuiButton-startIcon': {
								color: `${colors.buttons.primary.textColor} !important`
							},
							'& .MuiSvgIcon-root': {
								color: `${colors.buttons.primary.textColor} !important`
							},
							'&:hover': {
								color: `${colors.buttons.primary.textColor} !important`,
								backgroundColor: 'transparent !important'
							}
						}}
					>
						Add Field
					</Button>
				</Grid>
			</Grid>
		</Box>
	);
};
export default GroupingAlertTab;